/**
 * DREPage
 * 
 * Página de Demonstração do Resultado do Exercício (DRE).
 * Exibe DRE por competência com layout baseado em FinancialReports.md.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { 
  Download, ChevronDown, ChevronRight, ChevronLeft, 
  TrendingUp, Wallet, Info, CheckCircle2, Search,
  Clock, BrainCircuit, ArrowUpRight, LineChart,
  CalendarDays, Filter
} from 'lucide-react'
import { dreService } from '../../../../services/dreService'
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import DRECategoryTransactionsModal from './DRECategoryTransactionsModal'
import './DREPage.css'

function DREPage() {
  const { hasPermission } = usePermissions()
  const canViewDRE = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_DRE)
  const [loading, setLoading] = useState(true)
  const [dreData, setDreData] = useState(null)
  const [error, setError] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [categoryModal, setCategoryModal] = useState(null)

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ]

  const loadDRE = useCallback(async () => {
    if (!canViewDRE) {
      setDreData(null)
      setLoading(false)
      setError('Você não tem permissão para visualizar o DRE.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await dreService.getDRE(selectedMonth, selectedYear)
      if (response.success) {
        setDreData(response.data)
      } else {
        setError('Erro ao carregar DRE')
      }
    } catch (err) {
      console.error('Erro ao carregar DRE:', err)
      setError('Erro ao carregar DRE. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }, [selectedMonth, selectedYear, canViewDRE])

  useEffect(() => {
    loadDRE()
  }, [loadDRE])

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(val)
  }

  const toggleRow = (key) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    setExpandedRows(newExpanded)
  }

  const openCategoryModal = (child, sectionType) => {
    if (!child?.transactionCount) return
    setCategoryModal({
      category: child,
      sectionType
    })
  }

  const closeCategoryModal = () => {
    setCategoryModal(null)
  }

  // Montar estrutura de dados para exibição
  const reportLines = useMemo(() => {
    if (!dreData) return []

    const lines = []

    // Receita Operacional Líquida
    if (dreData.revenue) {
      lines.push({
        key: 'revenue',
        label: dreData.revenue.label,
        value: dreData.revenue.value,
        children: dreData.revenue.children,
        isTotal: false
      })
    }

    // CMV (já líquido de devoluções quando aplicável)
    if (dreData.costOfGoodsSold) {
      lines.push({
        key: 'cmv',
        label: dreData.costOfGoodsSold.label,
        value: dreData.costOfGoodsSold.value,
        isTotal: false,
        isNegative: true
      })
    }

    // Lucro Bruto
    if (dreData.grossProfit) {
      lines.push({
        key: 'grossProfit',
        label: dreData.grossProfit.label,
        value: dreData.grossProfit.value,
        isTotal: true
      })
    }

    // Despesas Operacionais
    if (dreData.operatingExpenses) {
      lines.push({
        key: 'opex',
        label: dreData.operatingExpenses.label,
        value: dreData.operatingExpenses.value,
        children: dreData.operatingExpenses.children,
        isTotal: false,
        isNegative: true
      })
    }

    // EBITDA
    if (dreData.ebitda) {
      lines.push({
        key: 'ebitda',
        label: dreData.ebitda.label,
        value: dreData.ebitda.value,
        isTotal: true
      })
    }

    // Resultado Financeiro
    if (dreData.financialResult) {
      lines.push({
        key: 'financial',
        label: dreData.financialResult.label,
        value: dreData.financialResult.value,
        isTotal: false,
        isNegative: true
      })
    }

    // Resultado Líquido
    if (dreData.netIncome) {
      lines.push({
        key: 'netIncome',
        label: dreData.netIncome.label,
        value: dreData.netIncome.value,
        isTotal: true,
        isFinal: true
      })
    }

    return lines
  }, [dreData])

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }

  const getMonthName = () => {
    return `${months[selectedMonth - 1]} ${selectedYear}`
  }

  const getPeriodRange = () => {
    if (!dreData?.period) return ''
    const start = new Date(dreData.period.startDate)
    const end = new Date(dreData.period.endDate)
    return `${start.getDate().toString().padStart(2, '0')} de ${months[start.getMonth()]} a ${end.getDate().toString().padStart(2, '0')} de ${months[end.getMonth()]}`
  }

  if (loading) {
    return (
      <div className="dre-page">
        <div className="dre-loading">
          <div className="dre-spinner"></div>
          <p>Carregando DRE...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="dre-page">
        <div className="dre-error">
          <p>{error}</p>
          <button onClick={loadDRE} className="dre-button-retry">Tentar novamente</button>
        </div>
      </div>
    )
  }

  return (
    <div className="dre-page">
      {/* Header */}
      <div className="dre-header">
        <div className="dre-header-left">
          <div className="dre-header-title">
            <div className="dre-header-icon">
              <CalendarDays size={20} />
            </div>
            <h1 className="dre-title">
              Relatório <span className="dre-title-accent">Mensal</span>
            </h1>
          </div>
          <p className="dre-subtitle">Consolidação de Resultados e Fluxo</p>
        </div>

        <div className="dre-header-actions">
          {/* Seletor de Mês */}
          <div className="dre-month-selector">
            <button 
              onClick={handlePreviousMonth}
              className="dre-month-button"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="dre-month-label">
              {getMonthName()}
            </span>
            <button 
              onClick={handleNextMonth}
              className="dre-month-button"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <button className="dre-button-icon">
            <Download size={20} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="dre-kpis">
        <div className="dre-kpi-card dre-kpi-primary">
          <div className="dre-kpi-glow"></div>
          <div className="dre-kpi-content">
            <p className="dre-kpi-label">
              <CheckCircle2 size={12} className="dre-kpi-icon-check" /> Lucro Líquido do Período
            </p>
            <p className={`dre-kpi-value ${dreData?.netIncome?.value >= 0 ? 'dre-kpi-positive' : 'dre-kpi-negative'}`}>
              {formatCurrency(dreData?.netIncome?.value || 0)}
            </p>
            <div className="dre-kpi-badge dre-kpi-badge-success">
              <TrendingUp size={14} /> +18.4% vs mês anterior
            </div>
          </div>
          <div className="dre-kpi-icon">
            <Wallet size={40} />
          </div>
        </div>

        <div className="dre-kpi-card dre-kpi-secondary">
          <div className="dre-kpi-glow dre-kpi-glow-amber"></div>
          <div className="dre-kpi-content">
            <p className="dre-kpi-label">
              Gap de Liquidez <Info size={12} className="dre-kpi-icon-info" />
            </p>
            <p className="dre-kpi-value dre-kpi-value-amber">
              {formatCurrency(0)}
            </p>
            <p className="dre-kpi-description">
              Pendente de recebimento (DRE vs DFC)
            </p>
          </div>
          <div className="dre-kpi-icon dre-kpi-icon-amber">
            <LineChart size={40} />
          </div>
        </div>
      </div>

      {/* Tabela de Composição */}
      <div className="dre-table-container">
        <div className="dre-table-header">
          <div className="dre-table-header-left">
            <h3 className="dre-table-title">Composição de Contas</h3>
            <p className="dre-table-subtitle">
              <Clock size={14} /> Ciclo Ativo: {getPeriodRange()}
            </p>
          </div>
          <div className="dre-table-header-actions">
            <div className="dre-search">
              <Search className="dre-search-icon" size={18} />
              <input 
                type="text" 
                placeholder="Localizar conta..." 
                className="dre-search-input"
              />
            </div>
            <button className="dre-button-filter">
              <Filter size={16} /> Filtros
            </button>
          </div>
        </div>

        <div className="dre-table-content">
          <div className="dre-table">
            {reportLines.map((line) => (
              <div key={line.key} className="dre-table-row-group">
                <div 
                  className={`dre-table-row ${line.isTotal ? 'dre-table-row-total' : ''} ${line.isFinal ? 'dre-table-row-final' : ''}`}
                >
                  <div className="dre-table-row-left">
                    {line.children && line.children.length > 0 ? (
                      <button 
                        onClick={() => toggleRow(line.key)}
                        className="dre-expand-button"
                      >
                        {expandedRows.has(line.key) ? (
                          <ChevronDown size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                      </button>
                    ) : (
                      <div className="dre-expand-spacer"></div>
                    )}
                    <span className={`dre-table-label ${line.isTotal ? 'dre-table-label-total' : ''}`}>
                      {line.label}
                    </span>
                  </div>
                  
                  <div className="dre-table-row-right">
                    <div className="dre-table-value-container">
                      <span className={`dre-table-value ${line.isNegative ? 'dre-table-value-negative' : ''} ${line.isTotal ? 'dre-table-value-total' : ''} ${line.isFinal ? 'dre-table-value-final' : ''}`}>
                        {formatCurrency(line.value)}
                      </span>
                    </div>
                    <button className="dre-table-action">
                      <ArrowUpRight size={20} />
                    </button>
                  </div>
                </div>
                
                {line.children && expandedRows.has(line.key) && (
                  <div className="dre-table-children">
                    {line.children.map((child, index) => (
                      <div key={index} className="dre-table-child-row">
                        <div className="dre-table-child-left">
                          <div className="dre-table-child-line"></div>
                          <span className="dre-table-child-label">{child.label}</span>
                        </div>
                        <div className="dre-table-child-right">
                          <span className={`dre-table-child-value ${child.value < 0 ? 'dre-table-value-negative' : ''}`}>
                            {formatCurrency(child.value)}
                          </span>
                          {line.key === 'opex' && (
                            <button
                              type="button"
                              className="dre-table-child-count-button"
                              onClick={() => openCategoryModal(child, 'opex')}
                              disabled={!child.transactionCount}
                              title="Ver lançamentos da categoria"
                            >
                              ({child.transactionCount || 0} transações)
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Insight Gemini */}
        <div className="dre-insight">
          <div className="dre-insight-left">
            <div className="dre-insight-icon">
              <BrainCircuit size={28} />
            </div>
            <div className="dre-insight-content">
              <h4 className="dre-insight-title">Parecer Analítico Gemini</h4>
              <p className="dre-insight-text">
                A margem bruta de <span className="dre-insight-highlight">{dreData?.grossProfit?.margin?.toFixed(0) || 0}%</span> está 
                <span className="dre-insight-success"> acima da média anual</span>. 
                O gap de liquidez sugere antecipação de recebíveis para otimizar o caixa.
              </p>
            </div>
          </div>
          <button className="dre-button-pdf">
            Extrair PDF Analítico
          </button>
        </div>
      </div>

      <DRECategoryTransactionsModal
        isOpen={Boolean(categoryModal)}
        onClose={closeCategoryModal}
        category={categoryModal?.category}
        sectionType={categoryModal?.sectionType}
        month={selectedMonth}
        year={selectedYear}
      />
    </div>
  )
}

export default DREPage
